import { pbQuery } from "@sergio9929/pb-query";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import type { SafeOmit } from "@/lib/utils";
import { type BranchesResponse, Collections, type InvitationsResponse } from "@/types/pocketbase.ts";
import type { FullListOptions, ListOptions, OneOptions } from "@/types/query-options";

const COLLECTION = Collections.Invitations;

export const invitationsQueries = {
	all: () => [COLLECTION],
	lists: () => [...invitationsQueries.all(), "list"],
	list: <Texpand = unknown>(params: ListOptions = { page: 1, perPage: 10, options: {} }) =>
		queryOptions({
			queryKey: [
				...invitationsQueries.lists(),
				{ page: params.page, perPage: params.perPage, options: params.options },
			],
			queryFn: () =>
				pb.collection(COLLECTION).getList<InvitationsResponse<Texpand>>(params.page, params.perPage, params.options),
			placeholderData: keepPreviousData,
		}),
	fullLists: () => [...invitationsQueries.lists(), "fullList"],
	fullList: <Texpand = unknown>(options?: FullListOptions) =>
		queryOptions({
			queryKey: [...invitationsQueries.fullLists(), options ?? {}],
			queryFn: () =>
				pb.collection(COLLECTION).getFullList<InvitationsResponse<Texpand>>({
					...options?.options,
					sort: options?.options?.sort ?? "-created",
				}),
			placeholderData: keepPreviousData,
		}),
	ones: () => [...invitationsQueries.all(), "one"],
	one: <Texpand = unknown>(params?: OneOptions) =>
		queryOptions({
			queryKey: [...invitationsQueries.ones(), params?.id, params?.options ?? {}],
			queryFn: () => pb.collection(COLLECTION).getOne<InvitationsResponse<Texpand>>(params?.id ?? "", params?.options),
			enabled: Boolean(params?.id),
		}),
	first: <Texpand = unknown>(params?: ListOptions) =>
		queryOptions({
			queryKey: [...invitationsQueries.ones(), params ?? {}],
			queryFn: () => pb.collection(COLLECTION).getFirstListItem<InvitationsResponse<Texpand>>("", params?.options),
		}),
	byToken: (token?: string) => {
		const invitation_token = token;
		type WithBranch = { branch: BranchesResponse };
		type Search = SafeOmit<InvitationsResponse, "branch"> & WithBranch;

		const dynamicQuery = pbQuery<Search>().expand("branch");

		if (invitation_token) {
			dynamicQuery.equal("token", invitation_token);
		}

		const query = dynamicQuery.build((raw, params) => pb.filter(raw, params));

		return queryOptions({
			queryKey: [...invitationsQueries.all(), "byToken", invitation_token ?? "", query],
			queryFn: () => {
				if (!invitation_token) return null;

				return pb.collection(COLLECTION).getFirstListItem<InvitationsResponse<WithBranch>>("", {
					...query,
					headers: { invitation_token },
				});
			},
			enabled: !!invitation_token,
		});
	},
};
